import { call, put, select, all, dispatch, takeEvery } from "redux-saga/effects";
import {
  getInteractionsPage,
  getAdditionalInfoPage,
  getAbsenceStatisticsFilters,
  getAbsenceDocumentsPage,
  sagaGetAbsencesFilters,
  getAlertsTriggeredPage,
  getEmployeeAbsenceNotificationsPage,
} from "@modules/shared/selectors/employee-absences";
import type { Action } from "redux-action";
import http from "@http";
import config from "@config";
import { toast } from "react-toastify";
import sharedActionCreators from "@modules/shared/actionCreator";
import reportingActionCreators from "@modules/reporting/actionCreator";
import {
  GetEmployeeAbsences,
  DeleteEmployeeAbsence,
  GetAbsenceTimeLost,
  GetEmployeeAbsence,
  GetEmployeeAbsencesSchema,
  GetAlertsTriggered,
  CancelAlert,
  CreateEmployeeAbsence,
  UpdateEmployeeAbsence,
  GetEmployeeAbsenceShiftPattern,
  GetEmployeeAbsenceShiftPatternOptions,
  UpdateAbsence,
  UpdateAbsenceSuccess,
  UpdateAbsenceError,
  CreateAbsence,
  CreateAbsenceSuccess,
  CreateAbsenceError,
  UpdateEmployeeAbsenceShiftPattern,
  ExportEmployeeAbsenceHistory,
  ExportEmployeeAbsenceHistorySuccess,
  GetEmployeeInteractions,
  UpdateEmployeeInteraction,
  GetEmployeeAbsenceAdditionalInfo,
  UpdateEmployeeAbsenceAdditionalInfo,
  GetAbsenceStatistics,
  GetEmployeeAbsenceDocuments,
  GetRevisionHistory,
  GetEmployeeSMSMessages,
  CreateEmployeeAbsenceDocument,
  DeleteEmployeeAbsenceDocument,
  GetManagerNotes,
  ExportManagerNotes,
  GetEmployeeAbsenceNotification,
  GetEmployeeAbsenceNotifications,
  GetAbsenceManagerNotesSchema,
  AddManagerNote,
  EditManagerNote,
  CancelRtwInterview,
  GetEmployeeNonCancelledAbsences,
  GetAbsenceNoteTypes,
  SubmitTransferAbsence,
  DeleteManagerNote,
  GetEmployeeAbsenceDocument,
  GetEmployeeBankHolidaysOptions,
  DeleteRtwInterview,
  RebuildRtwInterview,
  GetAbsenceCancelReasons,
  CancelEmployeeAbsence,
  GetEmployeeAbsenceById,
} from "@modules/shared/constants/actions";
import { EMPLOYEE_QUESTION_TYPE } from "@modules/shared/constants/employee-absences";
import { delay, downloadCsvFile, downloadFile } from "@sagas/utils";
import { dataURLtoFile } from "@utils/file";
import { transformFilters, transformSearchQuery, OPERATORS } from "@transformers/request";
import { sendSuccessMessage } from "@modules/app/actionCreator/index";
import { parseSchema } from "@utils/schema";
import { push } from "connected-react-router";
import { route } from "@routes/helper";
import { REPORTING_EMPLOYEE_ABSENCE_DETAILS_PATH } from "@routes/constants";

type EmployeeAbsencesRequest = {
  clientId: number,
  employeeId: number,
  page?: number,
  isReportingRequest?: boolean,
  limit?: number,
  sort?: string,
};
//#region Delete employee ABSENCE
export function* onDeleteEmployeeAbsence({
  payload,
}: Action<{ absenceId: number, clientId: number, employeeId: number }>): Generator<*, void, *> {
  const { absenceId, clientId, employeeId } = payload || {};

  try {
    const response = yield call(
      http.delete,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}/absence/${absenceId}`,
    );
    yield yield all([
      put(sharedActionCreators.GetEmployeeAbsences({ clientId, employeeId })),
      put(
        sharedActionCreators.DeleteEmployeeAbsenceSuccess({
          clientId: clientId,
          employeeId: employeeId,
        }),
      ),
    ]);
    toast.success("Absence record deleted");
  } catch (e) {
    yield put(sharedActionCreators.DeleteEmployeeAbsenceError(e.response));
    toast.error(e?.response?.data?.messages[0] || "Error");
  }
}

export function* onGetEmployeeAbsences({
  payload,
}: Action<EmployeeAbsencesRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  const filters = yield select(sagaGetAbsencesFilters);
  const transformedFilters = transformFilters(filters, {
    starts_at: OPERATORS.GREATER_THAN,
    rtw_at: OPERATORS.LESS_THAN,
  });

  try {
    const endpoint = payload.isReportingRequest
      ? `/api/v1/reports/client/${payload.clientId}/employee/${payload.employeeId}/absence-history`
      : `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence`;

    const response = yield call(http.get, endpoint, {
      params: {
        search: transformedFilters,
        limit: 1000,
        page: payload.page,
      },
    });

    yield put(sharedActionCreators.GetEmployeeAbsencesSuccess(response.data));
  } catch (e) {
    yield put(sharedActionCreators.GetEmployeeAbsencesError(e.response));
  }
}

type EmployeeAbsenceRequest = $Exact<{
  ...EmployeeAbsencesRequest,
  absenceId: number,
}>;

//#region onGetEmployeeAbsence
export function* onGetEmployeeAbsence({
  payload,
}: Action<EmployeeAbsenceRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.absenceId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  const { clientId, employeeId, absenceId } = payload;
  try {
    let endpoint = `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence`;
    let params = {
      params: {
        search: transformFilters({ id: payload.absenceId }),
        limit: payload.limit || 0,
        page: payload.page || 0,
        sort: payload.sort || "",
      },
    };

    if (payload.isReportingRequest) {
      endpoint = `/api/v1/reports/client/${payload.clientId}/employee/${payload.employeeId}/absence-history/${payload.absenceId}`;
      params = null;
    }

    const response = yield call(http.get, endpoint, params);
    const absence = payload.isReportingRequest ? response.data : response.data.content[0];

    //#region Time Lost injection
    const tLResponse = yield call(
      http.get,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}/absence/${absenceId}/time-lost`,
    );
    yield all([
      put(sharedActionCreators.GetEmployeeAbsenceSuccess(absence)),
      put(sharedActionCreators.GetAbsenceTimeLost({ ...payload })),
    ]);
  } catch (e) {
    yield put(sharedActionCreators.GetEmployeeAbsenceError(e.response));
  }
}

export function* onGetEmployeeAbsenceById({
  payload,
}: Action<EmployeeAbsenceRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.absenceId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}`,
    );

    yield all([put(sharedActionCreators.GetEmployeeAbsenceByIdSuccess(response.data))]);
  } catch (e) {
    yield put(sharedActionCreators.GetEmployeeAbsenceByIdError(e.response));
  }
}

const absencesSchemaExcludedProperties = ["createdAt", "createdBy", "updatedAt", "updatedBy"];

export function* onGetEmployeeAbsencesSchema({
  payload,
}: Action<EmployeeAbsencesRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const schema = yield parseSchema("EmployeeAbsenceDTO", { clientId: payload.clientId });

    absencesSchemaExcludedProperties.forEach(
      excludedProperty => delete schema.properties[excludedProperty],
    );
    yield put(sharedActionCreators.GetEmployeeAbsencesSchemaSuccess(schema));
  } catch (e) {
    yield put(sharedActionCreators.GetEmployeeAbsencesSchemaSuccess(e.response));
  }
}

type EmployeeAbsenceDetailsRequest = $Exact<{
  ...EmployeeAbsencesRequest,
  absenceId: number,
  alertId?: number,
  page?: number,
}>;

//#region Get Alerts triggered
export function* onGetAlertsTriggered({
  payload,
}: Action<EmployeeAbsenceDetailsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.absenceId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const currentPage = yield select(getAlertsTriggeredPage);
    const page = (payload && payload.page) || currentPage;

    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/alert`,
      {
        params: {
          page,
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(sharedActionCreators.GetAlertsTriggeredSuccess(response.data));
  } catch (e) {
    yield put(sharedActionCreators.GetAlertsTriggeredError(e.response));
  }
}

//#region Cancel Alert

export function* onCancelAlert({
  payload,
}: Action<EmployeeAbsenceDetailsRequest>): Generator<*, void, *> {
  if (
    !payload ||
    !payload.clientId ||
    !payload.employeeId ||
    !payload.alertId ||
    !payload.absenceId
  ) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }
  const { clientId, employeeId, absenceId, alertId } = payload;

  try {
    const response = yield call(
      http.post,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}/absence/${absenceId}/alert/${alertId}/cancel`,
    );

    yield all([
      put(sharedActionCreators.GetAlertsTriggered(payload)),
      put(sharedActionCreators.CancelAlertSuccess(response.data)),
    ]);
    toast.success("Alert cancelled");
  } catch (e) {
    yield put(sharedActionCreators.CancelAlertError(e.response));
    toast.error("Error canceling the alert");
  }
}

type CreateEmployeeAbsencesRequest = $Exact<{
  ...EmployeeAbsencesRequest,
  formData: Object,
  reloadListAfterUpdate?: boolean,
}>;

//#region Create Absence
export function* onCreateEmployeeAbsence({
  payload,
}: Action<CreateEmployeeAbsencesRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.formData) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    yield put(sharedActionCreators.SharedSetLoading());

    const response = yield call(
      http.post,
      `/api/v2/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence`,
      payload.formData,
    );
    const isActive = response.data.absenceReason.isActive;
    const absenceId = response.data.id;

    // Get list of possible patterns
    const empPosResponse = yield call(
      http.get,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/employee/${payload.employeeId}/employee-position/active`,
    );

    //#region Time Lost injection
    //Update the shift pattern
    const tLResponse = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${absenceId}/time-lost`,
    );

    yield all([
      put(sharedActionCreators.CreateEmployeeAbsenceSuccess(response.data)),
      put(
        sharedActionCreators.GetEmployeeAbsenceSuccess({
          ...response.data,
        }),
      ),
      put({
        type: "SHARED/GET_ABSENCE_TIME_LOST",
        payload: { ...payload, absenceId: response.data.id },
      }),

      put(
        sharedActionCreators.GetEmployeeAbsences({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(sharedActionCreators.SharedSetLoaded()),
    ]);
    toast.success("Absence Added Successfully.");
  } catch (e) {
    yield put(sharedActionCreators.SharedSetLoaded());
    yield put(sharedActionCreators.CreateEmployeeAbsenceError(e.response));
  }
}
export function* onGetAbsenceTimeLost({
  payload,
}: Action<{ clientId: number, employeeId: number, absenceId: number }>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.absenceId) {
    throw Error(`Invalid payload provided onGetAbsenceTimeLost ${JSON.stringify(payload)}`);
  }

  const { clientId, employeeId, absenceId } = payload;

  try {
    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}/absence/${absenceId}/time-lost`,
    );

    yield put(sharedActionCreators.GetAbsenceTimeLostSuccess(response.data));
  } catch (e) {
    yield put(sharedActionCreators.GetAbsenceTimeLostError(e.response));
  }
}

export function* onUpdateEmployeeAbsence({
  payload,
}: Action<CreateEmployeeAbsencesRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.formData) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.put,
      `/api/v2/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.formData.id}`,
      payload.formData,
    );

    yield all([
      put(sharedActionCreators.UpdateEmployeeAbsenceSuccess(response.data)),
      payload.reloadListAfterUpdate &&
        put(
          sharedActionCreators.GetEmployeeAbsences({
            clientId: payload.clientId,
            employeeId: payload.employeeId,
          }),
        ),
      put(
        sharedActionCreators.GetRevisionHistory({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
          absenceId: payload.formData.id,
        }),
      ),
    ]);
    yield toast.success("Absence Details Updated Successfully.");
  } catch (e) {
    yield put(sharedActionCreators.UpdateEmployeeAbsenceError(e.response));
  }
}

export function* onGetEmployeeAbsenceShiftPattern({
  payload,
}: Action<EmployeeAbsenceRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.absenceId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/time-lost`,
    );

    yield all([
      put(sharedActionCreators.GetEmployeeAbsenceShiftPatternSuccess(response.data)),
      put(
        sharedActionCreators.GetRevisionHistory({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
          absenceId: payload.absenceId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(sharedActionCreators.GetEmployeeAbsenceShiftPatternError(e.response));
  }
}

export function* onGetEmployeeAbsenceShiftPatternOptions({
  payload,
}: Action<EmployeeAbsencesRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const [schema, employeePositionList] = yield all([
      parseSchema("AbsenceTimeLostDTO", { clientId: payload.clientId }),
      call(
        http.get,
        `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/employee/${payload.employeeId}/employee-position`,
      ),
    ]);

    const options = {
      schema,
      employeePosition: employeePositionList.data.content,
    };

    yield put(sharedActionCreators.GetEmployeeAbsenceShiftPatternOptionsSuccess(options));
  } catch (e) {
    yield put(sharedActionCreators.GetEmployeeAbsenceShiftPatternOptionsError(e.response));
  }
}

type UpdateEmployeeDetailsRequest = $Exact<{
  ...EmployeeAbsenceDetailsRequest,
  formData: Array<Object>,
}>;

export function* onCreateAbsence({
  payload,
}: Action<UpdateEmployeeDetailsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.formData) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.post,
      `/api/v2/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence`,
      payload.formData,
    );

    yield all([
      put(sharedActionCreators.GetEmployeeAbsences({ ...payload })),
      put(sharedActionCreators.CreateAbsenceSuccess(response.data)),
    ]);
    toast.success("Absence Added Successfully.");
  } catch (e) {
    yield put(sharedActionCreators.CreateAbsenceError(e.response));
  }
}
export function* onUpdateAbsence({
  payload,
}: Action<UpdateEmployeeDetailsRequest>): Generator<*, void, *> {
  if (
    !payload ||
    !payload.clientId ||
    !payload.employeeId ||
    !payload.absenceId ||
    !payload.formData
  ) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.put,
      `/api/v3/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}`,
      payload.formData,
    );

    yield all([
      put(sharedActionCreators.GetEmployeeAbsences({ ...payload })),
      put(sharedActionCreators.UpdateAbsenceSuccess(response.data)),
    ]);
    toast.success("Absence Updated Successfully.");
  } catch (e) {
    yield put(sharedActionCreators.UpdateAbsenceError(e.response));
  }
}

export function* onUpdateEmployeeAbsenceShiftPattern({
  payload,
}: Action<UpdateEmployeeDetailsRequest>): Generator<*, void, *> {
  if (
    !payload ||
    !payload.clientId ||
    !payload.employeeId ||
    !payload.absenceId ||
    !payload.formData
  ) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.put,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/time-lost`,
      payload.formData,
    );

    yield all([
      put(sharedActionCreators.GetEmployeeAbsences({ ...payload })),
      put(sharedActionCreators.UpdateEmployeeAbsenceShiftPatternSuccess(response.data)),
      put(sharedActionCreators.GetEmployeeAbsenceShiftPatternSuccess(response.data)),
    ]);
    toast.success("Absence Shift Pattern Updated Successfully.");
  } catch (e) {
    yield put(sharedActionCreators.UpdateEmployeeAbsenceShiftPatternError(e.response));
  }
}

export function* onGetEmployeeInteractions({
  payload,
}: Action<EmployeeAbsenceDetailsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.absenceId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  const currentPage = yield select(getInteractionsPage);
  const page = (payload && payload.page) || currentPage;

  try {
    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/interaction`,
      {
        params: {
          page,
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(sharedActionCreators.GetEmployeeInteractionsSuccess(response.data));
  } catch (e) {
    yield put(sharedActionCreators.GetEmployeeInteractionsError(e.response));
  }
}

type UpdateEmployeeInteractionRequest = $Exact<{
  ...EmployeeAbsenceDetailsRequest,
  formData: Object,
  additionalInfo: Object,
}>;

export function* onUpdateEmployeeInteraction({
  payload,
}: Action<UpdateEmployeeInteractionRequest>): Generator<*, void, *> {
  if (
    !payload ||
    !payload.clientId ||
    !payload.employeeId ||
    !payload.absenceId ||
    !payload.formData
  ) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.put,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/interaction/${payload.formData.id}`,
      payload.formData,
    );

    yield all([
      put(sendSuccessMessage("GOODSHAPE_INTERACTION_UPDATED")),
      put(sharedActionCreators.UpdateEmployeeInteractionSuccess(response.data)),
      put(
        sharedActionCreators.GetEmployeeInteractions({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
          absenceId: payload.absenceId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(sharedActionCreators.UpdateEmployeeInteractionError(e.response));
  }
}

export function* onGetEmployeeAbsenceAdditionalInfo({
  payload,
}: Action<EmployeeAbsenceDetailsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.absenceId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  const currentPage = yield select(getAdditionalInfoPage);
  const page = (payload && payload.page) || currentPage;

  try {
    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/dynamic-question-answer`,
      {
        params: {
          page,
          limit: config.shortResultsPerPage,
        },
      },
    );

    let standardQuestions = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/standard-question-answer`,
      {
        params: {
          page,
          limit: config.shortResultsPerPage,
        },
      },
    );

    standardQuestions = standardQuestions.data.content.map(item => ({
      ...item,
      question: {
        ...item.question,
        captureConsentToShare: false,
        type: EMPLOYEE_QUESTION_TYPE.STANDARD,
      },
    }));
    const mergedQuestions = [...response.data.content, ...standardQuestions].sort((a, b) => {
      return new Date(b.createdAt) - new Date(a.createdAt);
    });

    yield put(
      sharedActionCreators.GetEmployeeAbsenceAdditionalInfoSuccess({
        ...response.data,
        content: [...mergedQuestions],
      }),
    );
  } catch (e) {
    yield put(sharedActionCreators.GetEmployeeAbsenceAdditionalInfoError(e.response));
  }
}

export function* onUpdateEmployeeAbsenceAdditionalInfo({
  payload,
}: Action<UpdateEmployeeInteractionRequest>): Generator<*, void, *> {
  if (
    !payload ||
    !payload.clientId ||
    !payload.employeeId ||
    !payload.absenceId ||
    !payload.formData ||
    !payload.additionalInfo
  ) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const absenceQuestionType = (payload.additionalInfo.data.question || {}).type;
    const endpoint =
      absenceQuestionType === EMPLOYEE_QUESTION_TYPE.STANDARD
        ? `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/standard-question-answer/${payload.formData.id}`
        : `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/dynamic-question-answer/${payload.formData.id}`;

    const response = yield call(http.put, endpoint, payload.formData);

    yield all([
      put(sendSuccessMessage("ADDITIONAL_INFO_UPDATED")),
      put(sharedActionCreators.UpdateEmployeeAbsenceAdditionalInfoSuccess(response.data)),
      put(
        sharedActionCreators.GetEmployeeAbsenceAdditionalInfo({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
          absenceId: payload.absenceId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(sharedActionCreators.UpdateEmployeeAbsenceAdditionalInfoError(e.response));
  }
}

type ExportAbsenceAbsenceHistoryRequest = $Exact<{
  clientId?: number,
  employee?: Object,
}>;

export function* onExportEmployeeAbsenceHistory({
  payload,
}: Action<ExportAbsenceAbsenceHistoryRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employee) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `api/v1/reports/client/${payload.clientId}/employee/${payload.employee.id}/absence-history/export`,
    );

    yield put(
      sharedActionCreators.ExportEmployeeAbsenceHistorySuccess({
        data: response.data,
        employee: payload.employee,
      }),
    );
  } catch (e) {
    yield put(sharedActionCreators.ExportEmployeeAbsenceHistoryError(e.response));
  }
}

type DownloadAbsenceHistoryExportFileRequest = $Exact<{
  data: Object,
  employee: Object,
}>;

export function* onExportEmployeeAbsenceHistorySuccess({
  payload,
}: Action<DownloadAbsenceHistoryExportFileRequest>): Generator<*, void, *> {
  if (!payload || !payload.data || !payload.employee) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  yield downloadCsvFile(
    payload.data,
    `${payload.employee.firstName}-${payload.employee.lastName}-absence-history.csv`,
  );
}

type AbsenceStatisticRequest = $Exact<{
  clientId?: number,
  employeeId?: number,
  filters?: $Exact<{
    fromDate: string,
    toDate: string,
    absenceType: string,
  }>,
}>;

export function* onGetAbsenceStatistics({
  payload,
}: Action<AbsenceStatisticRequest>): Generator<*, void, *> {
  try {
    const filters =
      payload && payload.filters ? payload.filters : yield select(getAbsenceStatisticsFilters);
    const transformedFilters = {
      from_date: filters.fromDate,
      to_date: filters.toDate,
      absence_types: [filters.absenceType],
    };

    let client, data;

    if (
      payload &&
      payload.clientId &&
      payload.employeeId &&
      transformedFilters.from_date &&
      transformedFilters.to_date &&
      transformedFilters.absence_types
    ) {
      const response = yield call(
        http.post,
        `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/statistics`,
        transformedFilters,
      );
      data = response.data;
    } else {
      const response = yield call(
        http.post,
        `/api/v1/my-details/my-absence/statistics`,
        transformedFilters,
      );
      data = response.data;
    }
    yield put(sharedActionCreators.GetAbsenceStatisticsSuccess(data));
  } catch (e) {
    yield put(sharedActionCreators.GetAbsenceStatisticsError(e.response));
  }
}

type EmployeeAbsenceDocumentsRequest = $Exact<{
  clientId: number,
  absenceId: number,
  page?: number,
}>;

export function* onGetEmployeeAbsenceDocuments({
  payload,
}: Action<EmployeeAbsenceDocumentsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.absenceId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  const currentPage = yield select(getAbsenceDocumentsPage);
  const page = (payload && payload.page) || currentPage;

  try {
    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/absence/${payload.absenceId}/document`,
      {
        params: {
          page,
          limit: config.shortResultsPerPage,
        },
      },
    );

    yield put(sharedActionCreators.GetEmployeeAbsenceDocumentsSuccess(response.data));
  } catch (e) {
    yield put(sharedActionCreators.GetEmployeeAbsenceDocumentsError(e.response));
  }
}

export function* onGetRevisionHistory({ payload }: Action<Object>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.absenceId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/absence-revision-history`,
    );
    yield put(sharedActionCreators.GetRevisionHistorySuccess(response.data));
  } catch (e) {
    yield put(sharedActionCreators.GetEmployeeAbsenceDocumentsError(e.response));
  }
}

export function* onGetEmployeeSMSMessages({ payload }: Action<Object>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.absenceId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/sms-history`,
      {
        params: {
          sort: "created_at,desc",
        },
      },
    );
    yield put(sharedActionCreators.GetEmployeeSMSMessagesSuccess(response.data));
  } catch (e) {
    yield put(sharedActionCreators.GetEmployeeSMSMessagesError(e.response));
  }
}

type CreateEmployeeAbsenceDocumentRequest = $Exact<{
  clientId: number,
  absenceId: number,
  document: Object,
}>;

export function* onCreateEmployeeAbsenceDocument({
  payload,
}: Action<CreateEmployeeAbsenceDocumentRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.absenceId || !payload.document) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const file = dataURLtoFile(payload.document.file);
    const data = new FormData();
    const documentType =
      payload.document.typeId?.id ||
      payload.document.typeId ||
      payload.document.type?.id ||
      payload.document.documentType;

    data.append("file", file.blob, decodeURIComponent(file.name));
    data.append("type.id", documentType);

    const response = yield call(
      http.post,
      `${config.endpointBaseUrl}api/v1/reports/client/${payload.clientId}/absence/${payload.absenceId}/document`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    yield all([
      put(sharedActionCreators.CreateEmployeeAbsenceDocumentSuccess(response.data)),
      put(
        sharedActionCreators.GetEmployeeAbsenceDocuments({
          clientId: payload.clientId,
          absenceId: payload.absenceId,
        }),
      ),
    ]);
    toast.success("Document Added Successfully.");
  } catch (e) {
    yield put(sharedActionCreators.CreateEmployeeAbsenceDocumentError(e.response));
  }
}

type DeleteEmployeeAbsenceDocumentRequest = $Exact<{
  clientId: number,
  absenceId: number,
  fileId: number,
}>;

export function* onDeleteEmployeeAbsenceDocument({
  payload,
}: Action<DeleteEmployeeAbsenceDocumentRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.absenceId || !payload.fileId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.delete,
      `/api/v1/reports/client/${payload.clientId}/absence/${payload.absenceId}/document/${payload.fileId}`,
    );

    yield all([
      put(sharedActionCreators.DeleteEmployeeAbsenceDocumentSuccess(response.data)),
      put(
        sharedActionCreators.GetEmployeeAbsenceDocuments({
          clientId: payload.clientId,
          absenceId: payload.absenceId,
        }),
      ),
      put(toast.success("Document Removed Successfully.")),
    ]);
  } catch (e) {
    yield put(sharedActionCreators.DeleteEmployeeAbsenceDocumentError(e.response));
  }
}

type EmployeeAbsenceDocumentRequest = $Exact<{
  ...DeleteEmployeeAbsenceDocumentRequest,
  fileName: string,
}>;

export function* onGetEmployeeAbsenceDocument({
  payload,
}: Action<EmployeeAbsenceDocumentRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.absenceId || !payload.fileId || !payload.fileName) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `api/v1/reports/client/${payload.clientId}/absence/${payload.absenceId}/document/${payload.fileId}`,
      {
        responseType: "blob",
      },
    );

    downloadFile(response.data, `${payload.fileId} ${payload.fileName}`);

    yield all([put(sharedActionCreators.GetEmployeeAbsenceDocumentSuccess(response.data))]);
  } catch (e) {
    yield put(sharedActionCreators.GetEmployeeAbsenceDocumentError(e.response));
  }
}

export function* onGetEmployeeAbsenceNotifications({
  payload,
}: Action<EmployeeAbsenceDetailsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.absenceId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  const currentPage = yield select(getEmployeeAbsenceNotificationsPage);
  const page = (payload && payload.page) || currentPage;

  try {
    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/notification`,
      {
        params: {
          page,
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(sharedActionCreators.GetEmployeeAbsenceNotificationsSuccess(response.data));
  } catch (e) {
    yield put(sharedActionCreators.GetEmployeeAbsenceNotificationsError(e.response));
  }
}

type EmployeeAbsenceNotificationRequest = $Exact<{
  ...EmployeeAbsenceDetailsRequest,
  notificationId: number,
  successMessage?: string,
}>;

export function* onGetEmployeeAbsenceNotification({
  payload,
}: Action<EmployeeAbsenceNotificationRequest>): Generator<*, void, *> {
  if (
    !payload ||
    !payload.clientId ||
    !payload.employeeId ||
    !payload.absenceId ||
    !payload.notificationId
  ) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/${payload.absenceId}/notification/${payload.notificationId}`,
    );

    yield all([
      put(sharedActionCreators.GetEmployeeAbsenceNotificationSuccess(response.data)),
      put(
        sharedActionCreators.ToggleEmployeeAbsenceNotificationModal({
          visible: true,
          notification: response.data,
        }),
      ),
    ]);
  } catch (e) {
    yield put(sharedActionCreators.GetEmployeeAbsenceNotificationError(e.response));
  }
}

export function* onGetAbsenceManagerNotesSchema(): Generator<*, void, *> {
  try {
    const schema = yield parseSchema("AbsenceManagerNoteDTO");

    yield all([put(sharedActionCreators.GetAbsenceManagerNotesSchemaSuccess(schema))]);
  } catch (e) {
    yield put(sharedActionCreators.GetAbsenceManagerNotesSchemaError(e.response));
  }
}

export type ManagerNote = $Exact<{
  absenceId?: number,
  noteType: {
    id: number,
  },
  notes: string,
}>;

export function* onGetManagerNotes({ payload }: Object): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }
  const page = payload.page || 1;

  const query = transformSearchQuery(
    { ...payload },
    {
      absenceId: {
        name: "absence",
        operator: OPERATORS.EQUALS,
      },
      toDate: {
        name: "updated_at",
        operator: OPERATORS.LESS_THAN,
      },
      fromDate: {
        name: "updated_at",
        operator: OPERATORS.GREATER_THAN,
      },
    },
  );

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/manager-note`,
      {
        params: {
          search: query,
          page,
          limit: config.resultsPerPage,
          sort: "created_at,desc",
        },
      },
    );

    yield put(sharedActionCreators.GetManagerNotesSuccess(response.data));
  } catch (e) {
    yield put(sharedActionCreators.GetManagerNotesError(e.response));
  }
}

export function* onExportManagerNotes({ payload }: Object): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }
  const page = payload.page || 0;

  const query = transformSearchQuery(
    { ...payload },
    {
      absenceId: {
        name: "absence",
        operator: OPERATORS.EQUALS,
      },
      toDate: {
        name: "updated_at",
        operator: OPERATORS.LESS_THAN,
      },
      fromDate: {
        name: "updated_at",
        operator: OPERATORS.GREATER_THAN,
      },
    },
  );

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/manager-note/export`,
      {
        params: {
          search: query,
          page,
          limit: config.results100PerPage,
        },
      },
    );
    const fromDate = payload.fromDate ? `From ${payload.fromDate}` : "";
    const toDate = payload.toDate ? `To ${payload.toDate}` : "";

    downloadCsvFile(response.data, `Absence Notes ${fromDate} ${toDate}.csv`);
    yield put(sharedActionCreators.ExportManagerNotesSuccess(response.data));
  } catch (e) {
    yield put(sharedActionCreators.ExportManagerNotesError(e.response));
  }
}

export function* onAddManagerNote({ payload }: Object): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.note) {
    throw Error("Invalid payload provided!");
  }
  try {
    yield call(
      http.post,
      `api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/manager-note`,
      payload.note,
    );

    yield all([
      put(sendSuccessMessage("NOTE_ADDED")),
      put(
        sharedActionCreators.GetManagerNotes({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
          absenceId: payload.absenceId,
        }),
      ),
      put(sharedActionCreators.AddManagerNoteSuccess()),
      put(sharedActionCreators.ToggleAddEditManagerNotesModal({ visible: false })),
    ]);
  } catch (e) {
    yield put(sharedActionCreators.AddManagerNoteError(e.response));
  }
}

export function* onEditManagerNote({ payload }: Object): Generator<*, void, *> {
  if (
    !payload ||
    !payload.clientId ||
    !payload.employeeId ||
    !payload.managerNoteId ||
    !payload.note
  ) {
    throw Error("Invalid payload provided!");
  }
  try {
    yield call(
      http.put,
      `api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/manager-note/${payload.managerNoteId}`,
      payload.note,
    );

    yield all([
      put(sendSuccessMessage("NOTE_UPDATED")),
      put(sharedActionCreators.EditManagerNoteSuccess()),
      put(
        sharedActionCreators.GetManagerNotes({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
          absenceId: payload.absenceId,
        }),
      ),
      put(sharedActionCreators.ToggleAddEditManagerNotesModal({ visible: false })),
    ]);
  } catch (e) {
    yield put(sharedActionCreators.EditManagerNoteError(e.response));
  }
}

export function* onDeleteManagerNote({ payload }: Action<Object>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.noteId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    yield call(
      http.delete,
      `api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/manager-note/${payload.noteId}`,
    );

    let acts = [
      put(sharedActionCreators.ToggleDeleteManagerNoteModal({ item: null })),
      put(sharedActionCreators.DeleteManagerNoteSuccess()),
      put(
        sharedActionCreators.GetManagerNotes({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
          absenceId: payload.absenceId,
        }),
      ),
    ];
    acts.push(put(sendSuccessMessage("NOTE_DELETED")));
    yield all(acts);
  } catch (e) {
    yield put(sharedActionCreators.DeleteManagerNoteError(e.response));
  }
}

export function* onCancelRtwInterviewTask({
  payload,
}: Action<{ clientId: number, task: Object }>): Generator<void, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.task) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield call(
      http.post,
      `${config.endpointBaseUrl}api/v1/tools/client/${payload.clientId}/tools/task/rtw-interview/${payload.task.id}/cancel`,
      null,
      {
        headers: {
          Accept: "application/json",
        },
      },
    );

    yield put(
      sharedActionCreators.GetEmployeeAbsence({
        clientId: payload.clientId,
        employeeId: payload.task.employee.id,
        absenceId: payload.task.absenceId,
        isReportingRequest: false,
      }),
    );
  } catch (e) {
    yield put(sharedActionCreators.CancelRtwInterviewError(e.response));
  }
}

export function* onSubmitTransferAbsence({
  payload,
}: Action<{
  clientId: number,
  employeeId: number,
  absenceId: number,
  employeeToId: number,
  employeePositionToId: number,
}>): Generator<void, void, *> {
  try {
    if (
      !payload ||
      !payload.clientId ||
      !payload.employeeId ||
      !payload.absenceId ||
      !payload.employeeToId ||
      !payload.employeePositionToId
    ) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const { clientId, employeeId, absenceId, employeeToId, employeePositionToId } = payload;
    const response = yield call(
      http.post,
      `api/v1/employee-admin/client/${clientId}/employee/${employeeId}/absence/${absenceId}/transfer-absence`,
      {
        employee_to_id: employeeToId,
        employee_position_to_id: employeePositionToId,
      },
    );

    yield all([
      put(
        sharedActionCreators.GetEmployeeAbsences({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(sharedActionCreators.SubmitTransferAbsenceSuccess(response.body)),
    ]);
  } catch (e) {
    yield put(sharedActionCreators.SubmitTransferAbsenceError(e.response));
  }
}

export function* onGetEmployeeNonCancelledAbsences({
  payload,
}: Action<Object>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  const { clientId, employeeId } = payload;

  try {
    const response = yield call(
      http.get,
      `api/v1/employee-admin/client/${clientId}/employee/${employeeId}/absence?search=is_cancelled:false&limit=100`,
    );
    yield put(sharedActionCreators.GetEmployeeNonCancelledAbsencesSuccess(response.data));
  } catch (e) {
    yield put(sharedActionCreators.GetEmployeeNonCancelledAbsencesError(e.response));
  }
}

export function* onGetAbsenceNoteTypes(): Generator<*, void, *> {
  try {
    const response = yield call(http.get, `${config.endpointBaseUrl}api/v1/data/absence-note-type`);
    yield put(sharedActionCreators.GetAbsenceNoteTypesSuccess(response.data));
  } catch (e) {
    yield put(sharedActionCreators.GetAbsenceNoteTypesError(e.response));
  }
}

export function* onGetEmployeeBankHolidaysOptions({
  payload,
}: Action<EmployeeAbsencesRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/client/${payload.clientId}/employee/${payload.employeeId}/data/bank-holidays`,
    );

    yield put(sharedActionCreators.GetEmployeeBankHolidaysOptionsSuccess(response.data));
  } catch (e) {
    yield put(sharedActionCreators.GetEmployeeBankHolidaysOptionsError(e.response));
  }
}

export function* onDeleteRtwInterviewTask({
  payload,
}: Action<{ clientId: number, taskId: number }>): Generator<void, void, *> {
  try {
    if (
      !payload ||
      !payload.clientId ||
      !payload.task ||
      !payload.employeeId ||
      !payload.absenceId
    ) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield call(
      http.delete,
      `${config.endpointBaseUrl}api/v1/tools/client/${payload.clientId}/tools/task/rtw-interview/${payload.task.id}`,
    );

    yield all([
      put(sharedActionCreators.DeleteRtwInterviewSuccess()),
      put(sendSuccessMessage("DELETE_RTW_INTERVIEW_SUCCESS")),
      put(reportingActionCreators.GetRtwInterviewTaskSuccess(null)),
      put(
        sharedActionCreators.GetEmployeeAbsence({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
          absenceId: payload.absenceId,
          isReportingRequest: true,
        }),
      ),
    ]);
  } catch (e) {
    yield put(sharedActionCreators.DeleteRtwInterviewError(e.response));
  }
}

export function* onRebuildRtwInterviewTask({
  payload,
}: Action<{ clientId: number, taskId: number }>): Generator<void, void, *> {
  try {
    if (
      !payload ||
      !payload.clientId ||
      !payload.task ||
      !payload.employeeId ||
      !payload.absenceId
    ) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield call(
      http.post,
      `${config.endpointBaseUrl}api/v1/tools/client/${payload.clientId}/tools/task/rtw-interview/${payload.task.id}/rebuild`,
    );

    yield all([
      put(sharedActionCreators.RebuildRtwInterviewSuccess()),
      put(sendSuccessMessage("DELETE_RTW_INTERVIEW_SUCCESS")),
      put(
        sharedActionCreators.GetEmployeeAbsence({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
          absenceId: payload.absenceId,
          isReportingRequest: true,
        }),
      ),
    ]);
  } catch (e) {
    yield put(sharedActionCreators.RebuildRtwInterviewError(e.response));
  }
}

export function* onGetAbsenceCancelReasons({ payload }: Action<{}>): Generator<*, void, *> {
  try {
    const response = yield call(http.get, `/api/v1/data/absence-cancel-reason`);
    yield put(sharedActionCreators.GetAbsenceCancelReasonsSuccess(response.data));
  } catch (e) {
    yield put(sharedActionCreators.GetAbsenceCancelReasonsError(e.response));
    toast.error(e?.response?.data?.messages[0] || "Error");
  }
}

export function* onCancelEmployeeAbsence({
  payload,
}: Action<{ absenceId: number, clientId: number, employeeId: number, data: Object }>): Generator<
  *,
  void,
  *,
> {
  const { absenceId, clientId, employeeId } = payload || {};

  try {
    yield call(
      http.post,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}/absence/${absenceId}/cancel`,
      payload.data,
    );

    yield all([
      put(sharedActionCreators.GetEmployeeAbsences({ clientId, employeeId })),
      put(
        sharedActionCreators.CancelEmployeeAbsenceSuccess({
          clientId: clientId,
          employeeId: employeeId,
        }),
      ),
    ]);
    toast.success("Absence canceled");
  } catch (e) {
    yield put(sharedActionCreators.CancelEmployeeAbsenceError(e.response));
    toast.error(e?.response?.data?.messages[0] || "Error");
  }
}

export default function* EmployeeAbsencesSaga(): Generator<*, void, *> {
  yield all([
    takeEvery(GetEmployeeAbsences, onGetEmployeeAbsences),
    takeEvery(DeleteEmployeeAbsence, onDeleteEmployeeAbsence),
    takeEvery(GetAbsenceCancelReasons, onGetAbsenceCancelReasons),
    takeEvery(CancelEmployeeAbsence, onCancelEmployeeAbsence),
    takeEvery(GetAbsenceTimeLost, onGetAbsenceTimeLost),
    takeEvery(GetEmployeeAbsence, onGetEmployeeAbsence),
    takeEvery(GetEmployeeAbsenceById, onGetEmployeeAbsenceById),
    takeEvery(GetEmployeeAbsencesSchema, onGetEmployeeAbsencesSchema),
    takeEvery(GetAlertsTriggered, onGetAlertsTriggered),
    takeEvery(CancelAlert, onCancelAlert),
    takeEvery(CreateEmployeeAbsence, onCreateEmployeeAbsence),
    takeEvery(UpdateEmployeeAbsence, onUpdateEmployeeAbsence),
    takeEvery(GetEmployeeAbsenceShiftPattern, onGetEmployeeAbsenceShiftPattern),
    takeEvery(GetEmployeeAbsenceShiftPatternOptions, onGetEmployeeAbsenceShiftPatternOptions),

    takeEvery(UpdateAbsence, onUpdateAbsence),
    takeEvery(CreateAbsence, onCreateAbsence),

    takeEvery(UpdateEmployeeAbsenceShiftPattern, onUpdateEmployeeAbsenceShiftPattern),
    takeEvery(ExportEmployeeAbsenceHistory, onExportEmployeeAbsenceHistory),
    takeEvery(ExportEmployeeAbsenceHistorySuccess, onExportEmployeeAbsenceHistorySuccess),
    takeEvery(GetEmployeeInteractions, onGetEmployeeInteractions),
    takeEvery(UpdateEmployeeInteraction, onUpdateEmployeeInteraction),
    takeEvery(GetEmployeeAbsenceAdditionalInfo, onGetEmployeeAbsenceAdditionalInfo),
    takeEvery(UpdateEmployeeAbsenceAdditionalInfo, onUpdateEmployeeAbsenceAdditionalInfo),
    takeEvery(GetAbsenceStatistics, onGetAbsenceStatistics),
    takeEvery(GetEmployeeAbsenceDocuments, onGetEmployeeAbsenceDocuments),
    takeEvery(GetRevisionHistory, onGetRevisionHistory),
    takeEvery(GetEmployeeSMSMessages, onGetEmployeeSMSMessages),
    takeEvery(CreateEmployeeAbsenceDocument, onCreateEmployeeAbsenceDocument),
    takeEvery(DeleteEmployeeAbsenceDocument, onDeleteEmployeeAbsenceDocument),
    takeEvery(GetEmployeeAbsenceDocument, onGetEmployeeAbsenceDocument),
    takeEvery(GetManagerNotes, onGetManagerNotes),
    takeEvery(ExportManagerNotes, onExportManagerNotes),
    takeEvery(GetEmployeeAbsenceNotification, onGetEmployeeAbsenceNotification),
    takeEvery(GetEmployeeAbsenceNotifications, onGetEmployeeAbsenceNotifications),
    takeEvery(GetAbsenceManagerNotesSchema, onGetAbsenceManagerNotesSchema),
    takeEvery(AddManagerNote, onAddManagerNote),
    takeEvery(EditManagerNote, onEditManagerNote),
    takeEvery(CancelRtwInterview, onCancelRtwInterviewTask),
    takeEvery(SubmitTransferAbsence, onSubmitTransferAbsence),
    takeEvery(GetEmployeeNonCancelledAbsences, onGetEmployeeNonCancelledAbsences),
    takeEvery(GetAbsenceNoteTypes, onGetAbsenceNoteTypes),
    takeEvery(DeleteManagerNote, onDeleteManagerNote),
    takeEvery(GetEmployeeBankHolidaysOptions, onGetEmployeeBankHolidaysOptions),
    takeEvery(DeleteRtwInterview, onDeleteRtwInterviewTask),
    takeEvery(RebuildRtwInterview, onRebuildRtwInterviewTask),
  ]);
}
